/*
 * AwareVizExtApp.ts
 * ===========
 * Public external version of AwareViz.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import * as firebaseApp from "firebase/app";
import * as auth from "firebase/auth";
import * as firestore from "firebase/firestore";
import { getAnalytics, logEvent } from "firebase/analytics";
import { configure, makeObservable, observable } from "mobx";
import { ActivityInsights } from "tsproto/aware_proto/proto/Insights";
import UserMap from "../awareviz/UserMap";
import AwareVizExtReactApp from "./AwareVizExtReactApp";
import { filterPhotoUrls, stripAddressFromPlaceNames } from "../awareviz/PeopleInsights";
const firebaseConfig = {
    apiKey: "AIzaSyAnekhAqDboucQz7N_P9EAOwDb-qwR6acU",
    authDomain: "viz.continuum.bot",
    projectId: "aware-viz-ext",
    storageBucket: "aware-viz-ext.firebasestorage.app",
    messagingSenderId: "843731484807",
    appId: "1:843731484807:web:d269907a7ec7767b43b822",
    measurementId: "G-67ENST66CF"
};
export default class AwareVizExtApp {
    authState = "unauthenticated";
    auth;
    analytics;
    refreshIdTokenTimeout;
    checkClaimsCount = 0;
    maxCheckClaimsRetries = 5;
    checkClaimsRetryDelayMs = 1500;
    userMap;
    activityInsights;
    constructor() {
        const app = firebaseApp.initializeApp(firebaseConfig);
        this.analytics = getAnalytics(app);
        this.auth = auth.getAuth();
        auth.getAuth().onAuthStateChanged(((firebaseUser) => {
            if (firebaseUser) {
                logEvent(this.analytics, "login", {
                    userUid: firebaseUser.uid,
                    method: "Google"
                });
                this.authState = "checking";
                this.checkClaims(firebaseUser);
            }
        }).bind(this));
        this.userMap = new UserMap();
        this.activityInsights = ActivityInsights.create({});
        makeObservable(this);
        configure({ enforceActions: "never" });
        new AwareVizExtReactApp(this);
    }
    checkClaims = (firebaseUser) => {
        if (this.checkClaimsCount >= this.maxCheckClaimsRetries) {
            this.authState = "denied";
            logEvent(this.analytics, "access_denied", { userUid: firebaseUser?.uid });
            return;
        }
        this.refreshIdToken().then(idTokenResult => {
            if (idTokenResult?.claims?.whitelisted) {
                this.authState = "whitelisted";
                const pLoadMap = this.userMap.initMapboxgl();
                this.getActivityInsights().then(activityInsights => {
                    pLoadMap.then(() => {
                        this.activityInsights = activityInsights;
                        logEvent(this.analytics, "content_loaded", { userUid: firebaseUser.uid });
                    });
                });
            }
            else {
                this.checkClaimsCount++;
                setTimeout(this.checkClaims, this.checkClaimsRetryDelayMs);
            }
        }).catch(error => {
            this.authState = "denied";
            logEvent(this.analytics, "access_denied", { userUid: firebaseUser?.uid });
        });
    };
    signIn() {
        const provider = new auth.GoogleAuthProvider();
        provider.setCustomParameters({
            prompt: "select_account"
        });
        auth.signInWithPopup(auth.getAuth(), provider);
        this.checkClaimsCount = 0;
    }
    signOut() {
        this.checkClaimsCount = 0;
        this.authState = "unauthenticated";
        clearTimeout(this.refreshIdTokenTimeout);
        this.refreshIdTokenTimeout = undefined;
        auth.signOut(auth.getAuth());
    }
    async getActivityInsights() {
        const db = firestore.getFirestore();
        const docName = "activityInsights-2025-10-30T03:44:51.534Z"; // "activityInsights"
        const chunksRef = firestore.collection(db, "data", docName, "chunks");
        const chunksQuery = firestore.query(chunksRef, firestore.orderBy("index", "asc"));
        const querySnapshot = await firestore.getDocs(chunksQuery);
        const chunks = [];
        querySnapshot.forEach(doc => {
            const blob = doc.data().data;
            const chunk = blob.toUint8Array();
            chunks.push(chunk);
        });
        const recombinedData = recombineChunks(chunks);
        const activityInsights = ActivityInsights.fromBinary(recombinedData);
        stripAddressFromPlaceNames(activityInsights);
        await filterPhotoUrls(activityInsights.personInsights);
        return activityInsights;
    }
    refreshIdToken = () => {
        clearTimeout(this.refreshIdTokenTimeout);
        this.refreshIdTokenTimeout = undefined;
        const firebaseUser = auth.getAuth().currentUser;
        if (firebaseUser) {
            const promise = firebaseUser.getIdTokenResult(true);
            promise.then(() => {
                this.refreshIdTokenTimeout = setTimeout(this.refreshIdToken, 55 * 60 * 1000); // 55 minutes
            }).catch(error => {
                this.authState = "denied";
            });
            return promise;
        }
        else {
            this.authState = "denied";
            return Promise.resolve(undefined);
        }
    };
}
__decorate([
    observable
], AwareVizExtApp.prototype, "authState", void 0);
__decorate([
    observable
], AwareVizExtApp.prototype, "activityInsights", void 0);
/**
 * Recombines an array of Uint8Array chunks into a single Uint8Array.
 *
 * @param chunks An array of Uint8Array chunks, assumed to be in the correct order.
 * @returns A single, combined Uint8Array.
 */
function recombineChunks(chunks) {
    // 1. Calculate the total size of the final array
    let totalLength = 0;
    for (const chunk of chunks) {
        totalLength += chunk.length;
    }
    // 2. Create a new, large Uint8Array to hold all the data
    const combinedArray = new Uint8Array(totalLength);
    // 3. Copy each chunk into the new array at the correct offset
    let offset = 0;
    for (const chunk of chunks) {
        combinedArray.set(chunk, offset);
        offset += chunk.length;
    }
    return combinedArray;
}
const app = new AwareVizExtApp();
if (process.env.NODE_ENV === "development") {
    window["app"] = app;
}
